import { useEffect, useState } from "react";
import { Flame, Mail, Sparkles, TrendingUp, Users } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const fmt = (n, digits) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD",
    minimumFractionDigits: digits, maximumFractionDigits: digits }).format(Number(n || 0));

const usd = (n) => fmt(n, 0);
const price = (n) => fmt(n, 2);

const Stat = ({ icon: Icon, label, value, hint, testid }) => (
  <div className="card-flat p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
      <Icon className="h-3.5 w-3.5" /> {label}
    </div>
    <p className="font-mono-num mt-3 text-3xl font-semibold">{value}</p>
    {hint && <p className="mt-1.5 text-xs text-slate-500">{hint}</p>}
  </div>
);

const Lancio = () => {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const load = () =>
      api
        .get("/admin/launch")
        .then(({ data }) => setData(data))
        .catch(() => setError(true));
    load();
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, []);

  if (error)
    return (
      <p className="text-sm text-red-600" data-testid="launch-error">
        {t("launch.error")}
      </p>
    );
  if (!data) return <p className="text-sm text-slate-500">{t("common.loading")}</p>;

  return (
    <div className="space-y-6" data-testid="launch-page">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight lg:text-3xl">
          {t("launch.title")}
        </h1>
        <p className="mt-1 text-sm text-slate-500">{t("launch.subtitle")}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          icon={Mail}
          label={t("launch.waitlist")}
          value={data.waitlist_total}
          hint={`${data.waitlist_converted} ${t("launch.converted")}`}
          testid="stat-waitlist"
        />
        <Stat
          icon={Sparkles}
          label={t("launch.passSold")}
          value={data.pass_sold}
          hint={`${price(data.pass_revenue_usd)} ${t("launch.gross")}`}
          testid="stat-pass"
        />
        <Stat
          icon={TrendingUp}
          label="MRR"
          value={usd(data.mrr_usd)}
          hint={`${data.pro_subscriptions} ${t("launch.proSubs")}`}
          testid="stat-mrr"
        />
        <Stat
          icon={Users}
          label={t("launch.users")}
          value={data.users_total}
          hint={`${data.ai_actions_month} ${t("launch.aiActions")}`}
          testid="stat-users"
        />
      </div>

      <div className="card-flat p-6" data-testid="seats-panel">
        <h2 className="text-base font-semibold md:text-lg">{t("launch.seats")}</h2>
        <p className="mt-1 text-sm text-slate-500">
          <b>
            {price(data.pass_price_usd)} · {data.pass_months} {t("launch.months")} ·{" "}
            {data.current_offer.seats_left} {t("plan.seatsLeft")} · {data.seats_reserved}{" "}
            {t("admin.reserved").toLowerCase()}
          </b>
        </p>
        <div className="mt-5">
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold">
              <Flame className="mr-1 inline h-3.5 w-3.5 text-amber-500" />
              {t("launch.passSold")}
            </span>
            <span className="font-mono-num text-slate-600">
              {data.pass_sold} / {data.pass_seats}
            </span>
          </div>
          <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full bg-indigo-600 transition-all duration-500"
              style={{
                width: `${Math.max(
                  Math.min(Math.round((data.pass_sold / data.pass_seats) * 100), 100),
                  data.pass_sold > 0 ? 3 : 0,
                )}%`,
              }}
            />
          </div>
        </div>
      </div>

      <div className="card-flat p-6">
        <h2 className="text-base font-semibold md:text-lg">{t("launch.costs")}</h2>
        <ul className="mt-3 space-y-2 text-sm text-slate-600">
          <li>{t("launch.cost1")}</li>
          <li>{t("launch.cost2")}</li>
          <li>{t("launch.cost3")}</li>
        </ul>
      </div>
    </div>
  );
};

export default Lancio;
